import { type ReactNode, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import { useAddressInfoByPostalCode, type AddressInfoByPostalCode } from "components/zipCloudApi/postalCodeApi";

interface PostalCodeSearchModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (address: AddressInfoByPostalCode) => void;
  initialSearchTerm?: string;
  subTitle?: ReactNode,
}

export default function PostalCodeSearchModal({
  open,
  onClose,
  onSelect,
  initialSearchTerm = "",
  subTitle,
}: PostalCodeSearchModalProps) {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [hasSearched, setHasSearched] = useState(false);
  const { getAddressInfoByPostalCode, isLoading, error, addressInfoList } = useAddressInfoByPostalCode();

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;

    try {
      await getAddressInfoByPostalCode(searchTerm);
      setHasSearched(true);
    } catch (error) {
      console.error("Error searching for address:", error);
      setHasSearched(true);
    }
  };

  const handleSelect = (address: AddressInfoByPostalCode) => {
    onSelect(address);
    onClose();
  };

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>郵便番号検索</DialogTitle>
      <DialogContent>
        {subTitle && (
          <Box sx={{ mb: 4 }}>
            {subTitle}
          </Box>
        )}
        <Box sx={{ display: "flex", mb: 2, mt: 1 }}>
          <TextField
            fullWidth
            label="郵便番号"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ mr: 1 }}
            placeholder="例: 123-4567 または 1234567"
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={isLoading || !searchTerm.trim()}
          >
            検索
          </Button>
        </Box>

        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Typography color="error" sx={{ my: 2 }}>
            エラーが発生しました: {error}
          </Typography>
        )}

        {hasSearched && !isLoading && addressInfoList.length === 0 && (
          <Typography sx={{ my: 2 }}>
            検索結果がありません。別の郵便番号で検索してください。
          </Typography>
        )}

        {!isLoading && addressInfoList.length > 0 && (
          <List sx={{ maxHeight: "400px", overflow: "auto" }}>
            {addressInfoList.map((address, index) => (
              <ListItem key={index} disablePadding divider>
                <ListItemButton onClick={() => handleSelect(address)}>
                  <ListItemText
                    primary={`〒${address.postalCode}`}
                    secondary={
                      <>
                        <Typography component="span" variant="body2" display="block">
                          {address.prefecture}
                          {address.city}
                          {address.address1}
                        </Typography>
                      </>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>キャンセル</Button>
      </DialogActions>
    </Dialog>
  );
}
